@let config = tableConfig();
@if (config.showHeader) {
  <div class="table-header">
    <table>
      <thead>
        <tr>
          @if (config.selectable) {
            <th class="select-all-th" width="38px">
              <input
                type="checkbox"
                aria-label="Select all rows"
                [checked]="allSelected()"
                [indeterminate]="someSelected()"
                (change)="toggleAll()"
                (click)="$event.stopPropagation()"
              />
            </th>
          }
          @for (col of config.columns; track trackColumn(col)) {
            @if (col.sortable && col.name && config.sortDirection && config.sortProperty) {
              <th
                mdwTableSortCell
                [sortProperty]="col.name"
                [tableSort]="{
                  sortProperty: config.sortProperty,
                  sortDirection: config.sortDirection,
                }"
                [width]="col.width ?? 'unset'"
                (sort)="sort.emit($event)"
              >
                {{ col.label }}
              </th>
            } @else {
              <th [width]="col.actions && col.actions.length > 0 ? '46px' : (col.width ?? 'unset')">
                @if (col.label) {
                  {{ col.label }}
                }
              </th>
            }
          }
        </tr>
      </thead>
    </table>
    @if (config.selectable && config.selectionActions) {
      <mdw-fade class="header-selection-overlay" [isVisible]="anySelected()">
        <div
          class="selection-toolbar hflex-200 flex-align-items-center"
          [class.selection-toolbar--surface-background]="selectionToolbarSurface() === 'Background'"
        >
          <span class="bold dense selection-count">{{ selectionLabel() }}</span>
          <div class="hflex-100 flex-align-items-center">
            @for (action of config.selectionActions; track action.name) {
              @let actionVisible =
                action.isVisible === undefined
                  ? true
                  : typeof action.isVisible === 'function'
                    ? action.isVisible(selectedItems())
                    : action.isVisible;
              @if (actionVisible) {
                @let isDisabled =
                  typeof action.disabled === 'function'
                    ? action.disabled(selectedItems())
                    : (action.disabled ?? false);
                @let actionIcon =
                  typeof action.icon === 'function' ? action.icon(selectedItems()) : action.icon;
                @let actionLabel =
                  typeof action.label === 'function' ? action.label(selectedItems()) : action.label;
                @let disabledTooltip =
                  typeof action.disabledTooltip === 'function'
                    ? action.disabledTooltip(selectedItems())
                    : action.disabledTooltip;
                @let actionTooltip =
                  isDisabled ? disabledTooltip : action.iconOnly ? actionLabel : undefined;
                <!--
                  The tooltip lives on this wrapper, not the button: a disabled
                  button has pointer-events: none, so hover never reaches it and
                  the tooltip would never show. The wrapper stays interactive
                  (events pass through the disabled button to it).
                -->
                <span class="selection-action" [matTooltip]="actionTooltip">
                  <button
                    class="mdw-button small-button"
                    [class.mdw-muted]="!action.iconOnly"
                    [class.mdw-tertiary]="action.iconOnly"
                    [icon]="actionIcon"
                    [disabled]="isDisabled"
                    [attr.aria-label]="action.iconOnly ? actionLabel : undefined"
                    (click)="emitSelectionAction(action.name)"
                  >
                    @if (!action.iconOnly) {
                      {{ actionLabel }}
                    }
                  </button>
                </span>
              }
            }
          </div>
        </div>
      </mdw-fade>
    }
  </div>
}
@if (config.items.length > 0 && !(loading() && loadingReplacesContent())) {
  @if (scrollStyle() === 'virtual') {
    <div
      class="virtual-scroll-container fill-width scrollable"
      cdkVirtualScrollingElement
      mdwScrollIsNearEdge
      [edgeToCheck]="scrollIsNearEdgeType()"
      [style.position]="'relative'"
      [style.right.px]="-scrollRightBuffer()"
      [style.left.px]="0"
      [style.paddingRight.px]="scrollRightBuffer()"
      [style.maxWidth]="'calc(100% + ' + scrollRightBuffer() + 'px)'"
      [style.width]="'calc(100% + ' + scrollRightBuffer() + 'px)'"
      [style.height]="config.showHeader ? 'calc(100% - 42px)' : '100%'"
      (nearBottomEdge)="nearBottomEdge.emit()"
      (nearTopEdge)="nearTopEdge.emit()"
    >
      <cdk-virtual-scroll-viewport
        class="fill-height fill-width"
        minBufferPx="200"
        maxBufferPx="400"
        [class.fill-height-without-header]="config.showHeader"
        [itemSize]="rowHeight()"
        [style.--item-size]="rowHeight() + 'px'"
      >
        <table>
          <thead class="hidden-header">
            <tr>
              @if (config.selectable) {
                <th width="38px"></th>
              }
              @for (col of config.columns; track trackColumn(col)) {
                <th
                  [width]="col.actions && col.actions.length > 0 ? '46px' : (col.width ?? 'unset')"
                ></th>
              }
            </tr>
          </thead>
          <tbody [class.has-scroll]="hasScroll()">
            <tr
              *cdkVirtualFor="let item of config.items; trackBy: config.trackBy ?? trackByIndex"
              [class.has-click-handler]="!!config.itemClick"
              [cdkContextMenuTriggerFor]="rowActionsMenu"
              [cdkContextMenuDisabled]="config.hideActions || rowActions().length === 0"
              (contextmenu)="actionsMenuItem.set(item)"
              (click)="config.itemClick && config.itemClick($event, item)"
            >
              <ng-container *ngTemplateOutlet="itemRow; context: { item }"></ng-container>
            </tr>
          </tbody>
        </table>
      </cdk-virtual-scroll-viewport>
    </div>
  } @else {
    <div
      class="fill-height scrollable"
      mdwScrollIsNearEdge
      [edgeToCheck]="scrollIsNearEdgeType()"
      [style.marginRight.px]="-scrollRightBuffer()"
      [style.paddingRight.px]="scrollRightBuffer()"
      [class.fill-height-without-header]="config.showHeader"
      (nearBottomEdge)="nearBottomEdge.emit()"
      (nearTopEdge)="nearTopEdge.emit()"
    >
      <table [style.--item-size]="rowHeight() === 'unset' ? 'unset' : rowHeight() + 'px'">
        <thead class="hidden-header">
          <tr>
            @if (config.selectable) {
              <th width="38px"></th>
            }
            @for (col of config.columns; track trackColumn(col)) {
              <th [width]="col.width ?? 'unset'"></th>
            }
          </tr>
        </thead>
        <tbody [class.has-scroll]="hasScroll()">
          @for (item of config.items; track trackBy($index, item)) {
            <tr
              [style.--row-padding]="(rowPadding() ?? 0) + 'px'"
              [class.has-click-handler]="!!config.itemClick"
              [cdkContextMenuTriggerFor]="rowActionsMenu"
              [cdkContextMenuDisabled]="config.hideActions || rowActions().length === 0"
              (contextmenu)="actionsMenuItem.set(item)"
              (click)="config.itemClick && config.itemClick($event, item)"
            >
              <ng-container *ngTemplateOutlet="itemRow; context: { item }"></ng-container>
            </tr>
          }
        </tbody>
      </table>
    </div>
  }
} @else if (loading()) {
  <div class="fill-height fill-width" [class.fill-height-without-header]="config.showHeader">
    <div
      class="vflex-000 fill-height fill-width flex-align-items-center flex-justify-content-center"
    >
      <mdw-loader></mdw-loader>
    </div>
  </div>
} @else {
  <div class="fill-height fill-width" [class.fill-height-without-header]="config.showHeader">
    <div
      class="fill-height fill-width vflex-075 flex-align-items-center flex-justify-content-center"
    >
      @let hasSearch = config.searchText && config.searchText.length > 0;
      @let icon = hasSearch || !config.zeroState?.icon ? icons.Search : $safeNavigationMigration(config.zeroState?.icon);
      @let zeroStateTitle =
        hasSearch ? 'No results for "' + config.searchText + '"' : $safeNavigationMigration(config.zeroState?.title);
      @let zeroStateDescription =
        hasSearch ? 'Try adjusting your search' : $safeNavigationMigration(config.zeroState?.description);
      @if (icon) {
        <mdw-icon [icon]="icon" class="no-data-icon x-large-icon secondary"></mdw-icon>
      }
      @if (zeroStateTitle) {
        <p class="bold">{{ zeroStateTitle }}</p>
      }
      @if (zeroStateDescription) {
        <small class="secondary">{{ zeroStateDescription }}</small>
      }
    </div>
  </div>
}

<ng-template #itemRow let-item="item">
  @if (config.selectable) {
    <td class="select-cell" (click)="$event.stopPropagation()">
      <input
        type="checkbox"
        [attr.aria-label]="isSelected(item) ? 'Deselect row' : 'Select row'"
        [checked]="isSelected(item)"
        (change)="toggleItem(item)"
      />
    </td>
  }
  @for (col of config.columns; track trackColumn(col)) {
    @if (col.actions && col.actions.length > 0) {
      <td class="action-cell">
        @if (!config.hideActions) {
          <button
            class="mdw-button mdw-muted small-button"
            [cdkMenuTriggerFor]="rowActionsMenu"
            [cdkMenuPosition]="[
              { originX: 'end', originY: 'bottom', overlayX: 'end', overlayY: 'top' },
            ]"
            [icon]="icons.Overflow"
            (click)="preventBubble($event); actionsMenuItem.set(item)"
          ></button>
        }
      </td>
    } @else if (col.customTemplate) {
      <td [class.allow-wrap]="col.allowCellTextWrap">
        <ng-container
          *ngTemplateOutlet="col.customTemplate; context: { $implicit: item }"
        ></ng-container>
      </td>
    } @else if (col.editable && col.name) {
      @let canEdit = col.editable.canEdit ? col.editable.canEdit(item) : true;
      <td
        mdwEditableCell
        [item]="item"
        [columnName]="col.name"
        [editConfig]="col.editable"
        [trackBy]="config.trackBy"
        [matTooltip]="
          (col.cellTooltip ? col.cellTooltip(item) : '') ||
          (canEdit ? 'Double-click to edit ' + col.label : '')
        "
        [class.allow-wrap]="col.allowCellTextWrap"
        [ngClass]="col.cellClass ? col.cellClass(item) : ''"
        (cellSaved)="cellEdited.emit($event)"
        (tabToNextCell)="handleCellNavigation($event)"
      >
        {{ item[col.name] }}
      </td>
    } @else {
      <td
        mdwTooltipIfClipped
        matTooltip
        [tooltip]="item[col.name]"
        [class.allow-wrap]="col.allowCellTextWrap"
        [ngClass]="col.cellClass ? col.cellClass(item) : ''"
      >
        {{ item[col.name] }}
      </td>
    }
  }
</ng-template>

<ng-template #rowActionsMenu>
  <menu
    class="surface-elevated pad-100 border-radius-100 open-left"
    cdkMenu
    animate.enter="menu-enter"
    animate.leave="menu-leave"
  >
    <ul>
      @if (actionsMenuItem(); as item) {
        @for (action of rowActions(); track action.name) {
          @let isHidden = action.hidden ? action.hidden(item) : false;
          @if (!isHidden) {
            @let isDisabled =
              action.disabled === true ? true : action.disabled ? action.disabled(item) : false;
            @let actionIcon = typeof action.icon === 'function' ? action.icon(item) : action.icon;
            @let actionLabel =
              typeof action.label === 'function' ? action.label(item) : action.label;
            @let disabledTooltip =
              typeof action.disabledTooltip === 'function'
                ? action.disabledTooltip(item)
                : action.disabledTooltip;
            <li
              cdkMenuItem
              [cdkMenuItemDisabled]="isDisabled"
              [matTooltip]="isDisabled ? disabledTooltip : undefined"
              (cdkMenuItemTriggered)="handleAction.emit({ action: action.name, item })"
            >
              <div class="hflex-100 flex-align-items-center item-action">
                @if (actionIcon) {
                  <mdw-icon class="small-icon" [icon]="actionIcon"></mdw-icon>
                }
                {{ actionLabel }}
              </div>
            </li>
          }
        }
      }
    </ul>
  </menu>
</ng-template>
